import { useState, useEffect } from "react";
import { useApi } from "./useApi";
import { getCustomerCategory, getSponsorUnicityId } from "../common/helper-functions";

// Resolves two facts about a sponsor, for the member access rule in
// MembersDrawer.jsx / DistributorsDrawer.jsx:
//
//   sponsorType       "partner" | "member" -- the sponsor's own account type
//   sponsorSponsorId  the sponsor's OWN sponsor's unicity id, as a string
//
// Fetching the type here -- rather than reading PartnersContext.contextUserType,
// the type of whichever distributor's page happens to be in view -- is what
// makes the access checks self-contained: they no longer depend on which
// /partners* URL the drawer was opened from, so the URL-anchoring redirect the
// drawers used to carry (and the /partners/search-results bug it caused, see
// GO-2724 bug tickets 86e28959e / 86e2893q3) is gone. See GO-3190.
//
// sponsorSponsorId is what GO-3345 round 2 added, to answer "is this member's
// sponsor one of MY OWN frontline members?" -- the grandparent hop. It needs a
// SECOND request because neither endpoint returns both halves:
//   customers?unicity=X&expand=customer  -> type + href, but never a sponsor field
//   {href}?expand=profilePicture         -> sponsor.id.unicity
// The second call is the one MemberContext already uses to populate
// customerHrefData, so the shape it returns is the shape getSponsorUnicityId is
// written against, rather than one assumed to exist.
//
// The hook name is now narrower than what it returns; kept as-is rather than
// renaming across both drawers on a hotfix line.
const useSponsorType = (sponsorUnicityId) => {
  const [sponsorType, setSponsorType] = useState("");
  const [sponsorSponsorId, setSponsorSponsorId] = useState(undefined);
  const [sponsorTypeKnown, setSponsorTypeKnown] = useState(false);
  const { sendRequest } = useApi();

  useEffect(() => {
    const abortController = new AbortController();

    if (!sponsorUnicityId) {
      setSponsorType("");
      setSponsorSponsorId(undefined);
      setSponsorTypeKnown(false);
      return;
    }

    setSponsorTypeKnown(false);
    setSponsorSponsorId(undefined);

    sendRequest({
      method: "get",
      endpoint: `customers?unicity=${sponsorUnicityId}&expand=customer`,
      abortController,
    })
      .then((res) => {
        const sponsor = res.data?.items?.[0];
        const type = getCustomerCategory(sponsor?.type);
        setSponsorType(type);

        // The grandparent hop only matters for a MEMBER sponsor. A partner
        // sponsor already decides the answer -- a distributor between the user
        // and the member blocks it however close that distributor is -- so a
        // second request there would change nothing.
        if (type !== "member" || !sponsor?.href) {
          setSponsorTypeKnown(true);
          return undefined;
        }

        return sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${sponsor.href}?expand=profilePicture`,
          abortController,
        })
          .then((hrefRes) => {
            setSponsorSponsorId(getSponsorUnicityId(hrefRes.data));
            setSponsorTypeKnown(true);
          })
          .catch(() => {
            // Resolved with no grandparent, so the two-level check fails
            // closed (denied) instead of spinning forever.
            setSponsorTypeKnown(true);
          });
      })
      .catch(() => {
        // Resolved either way -- an access check that never resolves would
        // spin forever instead of denying/allowing based on what we know.
        setSponsorTypeKnown(true);
      });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsorUnicityId]);

  return { sponsorType, sponsorSponsorId, sponsorTypeKnown };
};

export { useSponsorType };
